/**
 * @file chatStore.tsx
 * @description 聊天会话全局状态（Zustand）：
 * - 管理对话消息流、加载态、工具开关、节流限制、音频播放互斥、暂停/停止状态
 * - 提供创建用户消息、合并增量响应、溯源消息、历史加载等方法
 */
import { create } from 'zustand'
import { SEPARATOR_TEXT } from './constants'

import { produce } from 'immer'
import { ChatMessageType, CitationsType, LimitInfoType } from './types'

/**
 * 聊天 Store 状态
 */
export type chatState = {
  /** 是否处于运行中（生成中/请求中），影响输入与按钮可用 */
  running: boolean
  chatId: string // 应用id
  messages: ChatMessageType[] // 包含到目前为止的对话的消息列表。
  historyEnd: boolean // 没有更多历史纪录
  showGuideQuestion: boolean // 控制引导问题的显示状态
  /**
   * 1、根据短期记忆中最后轮次进行控制，
   * 2、时间控制范围内数量>=5,前端只取最后5轮，数量<=5时按时间控制
   */
  limitInfo: LimitInfoType
  /** 上传文件/图片的 loading 状态 */
  upIsLoading: boolean
  // 最后一条是否结束
  lastIsEndSuccess: boolean
  /** 已启用的工具ID集合 */
  tool_ids: number[]
  /** 工具总开关 */
  tool_enable: boolean
  //send消息通知状态
  is_fix?: boolean // is_fix是否编辑 正常发送false 编辑true
  hisType?: boolean // true:历史记录 false:会话记录
  audioPlayingId: string | null // 当前正在播放的音频消息ID
  isPaused?: boolean // 是否停止生成
  stopVideoTaskPolling?: () => void // 停止视频任务轮询的函数
}

/**
 * 动作集合（对外方法）
 */
type Actions = {
  /**
   * 更新当前 chatId 并重置会话状态
   * @param chatId 应用/助手ID
   */
  updateChatId: (chatId: string) => void
  /**
   * 会话获取历史记录
   * @param history 历史数据（含 messages 数组）
   * @param chatId 目标会话ID
   */
  loadHistoryMsg: (history: any, chatId: string) => Promise<void>
  /**
   * 加载更多历史记录并追加
   * @param chatId 目标会话ID（预留，实际未使用）
   */
  loadMoreHistoryMsg: (chatId: string) => Promise<void>
  /** 销毁并重置当前会话状态 */
  destroyChat: () => void
  /**
   * 新建一条用户输入消息（文本/图片/文件），必要时合并至上一条用户消息
   * @param type 消息类型
   * @param text 文本内容
   * @param img 图片URL
   * @param imgName 图片名称
   * @param fileUrl 文件URL
   * @param fileName 文件名称
   * @param fake 是否为占位/临时消息
   * @param originalUrl 原图URL
   * @param material_info 素材信息list
   */
  createSendMsg: (
    type: 'text' | 'image_url' | 'file_url' | 'material',
    text?: string,
    img?: string,
    imgName?: string,
    fileUrl?: string,
    fileName?: string,
    fileId?: any,
    fake?: boolean,
    originalUrl?: string,
    material_info?: any
  ) => void
  /**
   * 直接追加一对（用户/助手）消息
   * @param userMessage 用户文本
   * @param assistantMessage 助手文本或渲染节点
   * @param hitPlugin 命中插件信息
   */
  addMessagePair: (userMessage: string, assistantMessage: string | JSX.Element, hitPlugin?: any) => void
  /**
   * 开启新一轮对话，插入系统分隔消息
   * @param ignore 是否在 UI 忽略展示
   */
  startNewRound: (ignore?: boolean) => void
  /** 追加 loading 占位消息 */
  updateLoadingMessage: () => void
  /**
   * 合并/更新当前助手消息（文本增量）
   * @param chatId 目标会话ID
   * @param text 增量文本
   * @param status 状态码
   * @param internetCitations 溯源（联网/工具/知识库）
   * @param clear 是否清空之前内容
   * @param think 增量思考文本
   * @param thinkTime 思考时长（ms）
   * @param chatStopTag 停止标记
   * @param hitPlugin 命中插件数据
   * @param thinkInfo 思考状态信息（例如已停止）
   */
  updateCurrentMessage: (
    chatId: string,
    text: string,
    status: number,
    internetCitations?: any,
    clear?: boolean,
    think?: string,
    thinkTime?: number,
    chatStopTag?: string,
    hitPlugin?: any,
    thinkInfo?: any
  ) => void
  /**
   * 合并/更新当前助手消息（图片与文本）
   * @param imgUrl 图片对象
   * @param chatId 会话ID
   * @param text 增量文本
   * @param status 状态码
   * @param internetCitations 溯源
   * @param clear 是否清空之前内容
   * @param think 增量思考文本
   * @param thinkTime 思考时长（ms）
   * @param chatStopTag 停止标记
   */
  updateCurrentMessageImage: (
    imgUrl: {
      type: string
      fileContextId?: string
      filename?: string
      url?: string
    },
    chatId: string,
    text: string,
    status: number,
    internetCitations?: any,
    clear?: boolean,
    think?: string,
    thinkTime?: number,
    chatStopTag?: string
  ) => void
  /**
   * 更新/清空溯源消息
   * @param chatId 会话ID
   * @param citation 新增溯源项
   * @param clear truthy 则清空
   */
  updateCitationsMessage: (chatId: string, citation: CitationsType | any, clear?: any) => void
  /**
   * 追加系统引导消息
   * @param text 文本
   * @param ignore 是否忽略展示
   */
  updateGuideMessage: (text: string, ignore?: boolean) => void
  /** 设置是否展示引导问题 */
  setShowGuideQuestion: (status: boolean) => void
  /** 更新上下文记忆限制参数 */
  updateLimitInfo: (limitInfo: LimitInfoType) => void
  /** 设置运行态（影响 UI 交互） */
  setIsRunning: (running: boolean) => void
  /** 设置上传 loading */
  setUpIsLoading: (loading: boolean) => void
  /** 设置最后一条是否正常结束 */
  setLastIsEndSuccess: (status: boolean) => void
  /** 写入工具ID集合 */
  setToolIds: (tool_ids: number[]) => void
  /** 设置工具开关 */
  setToolEnabled: (status: boolean) => void
  /** 切换历史/会话标记 */
  setHisType: (status: boolean) => void
  /** 是否处于编辑发送 */
  setIsFix: (status: boolean) => void
  /** 是否处于停止/暂停生成 */
  setPaused: (status: boolean) => void
  /** 设置当前正在播放的音频消息ID（互斥播放） */
  setAudioPlayingId: (id: string | null) => void
  /** 设置停止视频任务轮询的函数 */
  setStopVideoTaskPolling: (stopFn: (() => void) | undefined) => void
  /**
   * 更新视频任务状态
   * @param chatId 会话ID
   * @param generateVideo 视频任务信息
   */
  updateVideoTaskStatus: (
    chatId: string,
    generateVideo: {
      task_status?: string
      url?: string | null
      name?: string | null
      is_valid?: number
      task_id?: string
      thumbnail_url?: string | null
    }
  ) => void
}

/**
 * 初始化默认状态
 */
export const getDefaultChatState = () => {
  return {
    running: false,
    chatId: '',
    messages: [],
    historyEnd: false,
    showGuideQuestion: false,
    limitInfo: {
      remember_mode: 0, // 0 最后轮次，1 最后分钟数
      remember_last_num: 5,
      remember_last_minute: 60
    },
    upIsLoading: false,
    lastIsEndSuccess: false,
    tool_ids: [],
    tool_enable: false,
    is_fix: false, //正常发送false 编辑true
    hisType: false, // true:历史记录 false:会话记录
    isPaused: false, // 是否停止生成
    audioPlayingId: null, // 当前正在播放的音频消息ID
    stopVideoTaskPolling: undefined // 停止视频任务轮询的函数
  }
}

/**
 * 会话信息存储（Zustand）
 */
export const useChatStore = create<chatState & Actions>((set, get) => ({
  ...getDefaultChatState(),
  /**
   * 切换 chatId：重置为默认状态并写入新 chatId
   */
  updateChatId(chatId) {
    console.log('>>>changeChatId: ', chatId)
    set({ ...getDefaultChatState(), chatId })
  },

  destroyChat() {
    set({ ...getDefaultChatState() })
  },
  // todo history相关与后端确认,需提供与当前chat数据格式接口一致的接口
  /**
   * 加载历史消息：
   * - 当传入的 chatId 与当前不同，直接覆盖为历史；否则为追加
   */
  /**
   * 加载历史记录
   * @param history 历史数据（需包含 messages 数组）
   * @param chatId 目标会话ID
   */
  async loadHistoryMsg(history: any, chatId: string) {
    try {
      const currentChatId = get().chatId
      set(state => ({
        messages: chatId !== currentChatId ? [...history.messages] : [...state.messages, ...history.messages]
      }))
    } catch (error) {
      console.error('Failed to load history messages:', error)
    }
  },

  /**
   * 追加加载更多历史
   * @param history 历史数据（需包含 messages 数组）
   */
  async loadMoreHistoryMsg(history: any) {
    // 追加更多历史
    try {
      // const currentPage = Math.ceil(get().messages.length / 5) + 1
      // const history = await getHistoryMessagesApi(chatId, currentPage, 5)
      set(state => ({
        messages: [...state.messages, ...history.messages]
        // historyEnd: history.total <= state.messages.length + history.messages.length
      }))
    } catch (error) {
      console.error('Failed to load more history messages:', error)
    }
  },

  /**
   * 新建一条用户消息（必要时合并到上一条用户消息）
   * @param type 消息类型：'text' | 'image_url' | 'file_url' | 'material'
   * @param text 文本内容
   * @param img 图片地址
   * @param imgName 图片名称
   * @param fileUrl 文件下载/预览地址
   * @param fileName 文件名称
   * @param fake 是否为占位/临时消息（不进入正式流）
   * @param originalUrl 原图地址（与 img 区分裁剪图）
   * @param material_info 素材信息list
   */
  createSendMsg(
    type: 'text' | 'image_url' | 'file_url' | 'material',
    text: string,
    img: string,
    imgName: string,
    fileUrl: string = '',
    fileName: string = '',
    fileId: any = '',
    fake: boolean = false,
    originalUrl: string,
    material_info: any
  ) {
    const messages = get().messages.filter(msg => msg.role !== 'loading')
    // 同一批输入的图片和文本合并为一条消息中多个content
    const lastMessage = messages[messages.length - 1]
    const isLastUserAssistant = lastMessage?.role === 'user'

    // 按 type 只携带对应的必要字段，避免发给后端的消息体中出现大量空字段
    // type:'text' 时 text 字段始终保留（即使为空），其他类型有值才带
    const newContent: any = { type }
    if (type === 'text') {
      newContent.text = text
    } else if (text) {
      // 非 text 类型但意外带了文本内容时也保留
      newContent.text = text
    }
    if (type === 'image_url') {
      const imgObj: any = { url: img, detail: 'auto', name: imgName }
      if (originalUrl) imgObj.originalUrl = originalUrl
      newContent.image_url = imgObj
    } else if (type === 'file_url') {
      const fileObj: any = { url: fileUrl, name: fileName }
      if (fileId) fileObj.file_id = fileId
      newContent.file_url = fileObj
    } else if (type === 'material') {
      newContent.material_info = material_info || []
    }
    let newMessage: ChatMessageType = {
      role: 'user',
      content: [newContent],
      createTime: Date.now()
    }
    if (fake) {
      newMessage.fake = true
    }
    if (isLastUserAssistant) {
      newMessage = produce(lastMessage, (draft: ChatMessageType) => {
        draft.content = [...draft.content, newContent]
      })
    }
    set(state => ({
      messages: isLastUserAssistant ? [...messages.slice(0, -1), newMessage] : [...messages, newMessage]
    }))
  },
  /**
   * 追加 loading 占位消息（用于流式开始前）
   */
  /**
   * 追加 loading 占位消息
   * @returns void
   */
  updateLoadingMessage() {
    const loadingMessage = {
      role: 'loading',
      content: [
        {
          type: 'text',
          text: ''
        }
      ],
      ignore: true
    }
    set(state => ({ messages: [...state.messages, loadingMessage] }))
  },
  /**
   * 合并/更新当前助手文本消息（流式增量）
   * @param chatId 会话ID（不匹配则忽略）
   * @param content 增量文本内容
   * @param status 当前状态码（AgentStatus）
   * @param internetCitations 溯源信息数组（如联网/工具/知识库）
   * @param clear 是否清空之前累积内容（作为重置）
   * @param think 增量思考内容
   * @param thinkTime 思考时长（毫秒）
   * @param chatStopTag 停止标记（'stop' 时不再累积）
   * @param hitPlugin 命中插件信息（通知类）
   * @param thinkInfo 思考状态信息（如 type:'stop' 文案等）
   */
  updateCurrentMessage(
    chatId: string,
    content: string,
    status,
    internetCitations = [],
    clear = false,
    think = '',
    thinkTime = 0,
    chatStopTag: string,
    hitPlugin = {},
    thinkInfo = {}
  ) {
    const currentChatId = get().chatId
    if (chatId !== currentChatId) {
      return
    }
    // 如果已停止，阻止任何新的文本更新
    const { isPaused } = get()
    // if (isPaused) {
    //   return
    // }
    const messages = get().messages.filter(msg => msg.role !== 'loading')
    // 同一批响应的文本合并为一条消息
    const lastMessage = messages[messages.length - 1]
    const isLastMessageAssistant = lastMessage?.role === 'assistant'

    // 停止消息时不累积文本，避免重复
    const shouldAccumulate = isLastMessageAssistant && !clear && chatStopTag !== 'stop'
    let newTextContent = shouldAccumulate ? lastMessage.content[0].text + content : content

    const newCitations = !clear ? internetCitations : []
    let newThink = shouldAccumulate ? lastMessage.content[0].think + think : think
    /* 暂停就不需要添加content 追加后续文字 */
    if (isPaused) {
      newTextContent = lastMessage.content[0].text
      newThink = lastMessage.content[0].think
    }
    const newThinkTime =
      isLastMessageAssistant && lastMessage.content[0].thinkTime ? lastMessage.content[0].thinkTime : thinkTime
    // 如果 thinkInfo 包含 generate_video，添加到 content 中
    const generateVideo = thinkInfo?.generate_video

    const newMessage = {
      role: 'assistant',
      content: [
        {
          type: 'text',
          text: newTextContent,
          internetCitations: newCitations,
          think: newThink,
          thinkTime: newThinkTime,
          // 判断生成停止 显示文案 = 已停止生成
          chatStopTag,
          //@消息通知数据
          hitPlugin,
          /* 判断停止 - 深度思考停止状态，跟停止会话状态冲突 */
          thinkInfo,
          // 视频生成任务信息
          ...(generateVideo ? { generate_video: generateVideo } : {})
        }
      ],
      createTime: Date.now(),
      status
    }

    const newMessages = isLastMessageAssistant ? [...messages.slice(0, -1), newMessage] : [...messages, newMessage]
    set(state => ({ messages: newMessages }))
  },
  // 将图片信息追加到正在接收的message对应content中
  /**
   * 合并/更新当前助手图片消息（附带文本增量）
   * @param imgUrl 图片对象（含 url/filename 等）
   * @param chatId 会话ID（不匹配则忽略）
   * @param content 增量文本内容
   * @param status 当前状态码
   * @param internetCitations 溯源信息数组
   * @param clear 是否清空之前内容
   * @param think 增量思考内容
   * @param thinkTime 思考时长（毫秒）
   * @param chatStopTag 停止标记
   */
  updateCurrentMessageImage(
    imgUrl: {
      type: string
      fileContextId?: string
      filename?: string
      url?: string
    },
    chatId: string,
    content: string,
    status,
    internetCitations = [],
    clear = false,
    think = '',
    thinkTime = 0,
    chatStopTag: string
  ) {
    const currentChatId = get().chatId
    if (chatId !== currentChatId) {
      return
    }
    const messages = get().messages.filter(msg => msg.role !== 'loading')
    // 同一批响应的文本合并为一条消息
    const lastMessage = messages[messages.length - 1]
    const isLastMessageAssistant = lastMessage?.role === 'assistant'

    // 停止消息时不累积文本，避免重复
    const shouldAccumulate = isLastMessageAssistant && !clear && chatStopTag !== 'stop'
    const newTextContent = shouldAccumulate ? lastMessage.content[0].text + content : content
    const newCitations = !clear ? internetCitations : []
    const newThink = shouldAccumulate ? lastMessage.content[0].think + think : think
    const newThinkTime =
      isLastMessageAssistant && lastMessage.content[0].thinkTime ? lastMessage.content[0].thinkTime : thinkTime
    const newImgUrl =
      isLastMessageAssistant && lastMessage.content[0].image_url ? lastMessage.content[0].image_url : imgUrl

    const newMessage = {
      role: 'assistant',
      content: [
        {
          type: 'image_url',
          text: newTextContent,
          internetCitations: newCitations,
          think: newThink,
          thinkTime: newThinkTime,
          image_url: newImgUrl,
          chatStopTag
        }
      ],
      createTime: Date.now(),
      status
    }

    const newMessages = isLastMessageAssistant ? [...messages.slice(0, -1), newMessage] : [...messages, newMessage]
    set(state => ({ messages: newMessages }))
  },
  /**
   * 更新/清空溯源信息消息块：
   * - role: 'citations' 作为独立消息块插入
   * - clear 时传空数组
   */
  /**
   * 更新/清空溯源消息
   * @param chatId 当前会话ID
   * @param citation 新增溯源项
   * @param clear 传任意 truthy 值则清空
   */
  updateCitationsMessage(chatId: string, citation: CitationsType, clear?: string) {
    /* clear = 暂停会话清除命中效果 */
    const currentChatId = get().chatId
    if (chatId !== currentChatId) {
      return
    }
    const messages = get().messages.filter(msg => msg.role !== 'loading')
    const lastMessage = messages[messages.length - 1]
    const isLastMessageAssistant = lastMessage?.role === 'citations'

    const citationsMsg = {
      role: 'citations',
      // citations: [citation],
      citations: clear ? [] : isLastMessageAssistant ? [...lastMessage.citations, citation] : [citation],
      ignore: true
    }
    // const messages = get().messages
    // const newMessage = messages.filter(msg => msg.role !== 'loading')
    // set(state => ({ messages: [...messages, citationsMsg] }))
    const newMessages = isLastMessageAssistant ? [...messages.slice(0, -1), citationsMsg] : [...messages, citationsMsg]
    set(state => ({ messages: newMessages }))
    get().updateLoadingMessage()
  },
  /**
   * 开启新一轮（系统分隔消息）
   */
  /**
   * 开启新一轮，插入系统分隔消息
   * @param ignore 是否在 UI 忽略展示
   */
  startNewRound(ignore = false) {
    set(state => ({
      showGuideQuestion: true,
      messages: [
        ...state.messages,
        {
          role: 'system',
          content: [
            {
              type: 'text',
              text: SEPARATOR_TEXT
            }
          ],
          ignore
        }
      ]
    }))
  },
  /**
   * 追加一条系统引导消息
   */
  /**
   * 追加系统引导消息
   * @param text 文本内容
   * @param ignore 是否在 UI 忽略展示
   */
  updateGuideMessage(text, ignore = false) {
    set(state => ({
      messages: [
        ...state.messages,
        {
          role: 'system',
          content: [
            {
              type: 'text',
              text
            }
          ],
          ignore
        }
      ]
    }))
  },
  setShowGuideQuestion(status: boolean) {
    set({ showGuideQuestion: status })
  },
  updateLimitInfo(limitInfo) {
    set(state => ({
      limitInfo: { ...limitInfo }
    }))
  },
  // 运行中状态（影响发送/开关可用）
  setIsRunning(running: boolean) {
    set({ running })
  },
  // 上传 loading
  setUpIsLoading(loading: boolean) {
    set({ upIsLoading: loading })
  },
  // 最后一条是否正常结束
  setLastIsEndSuccess(success: boolean) {
    set({ lastIsEndSuccess: success })
  },
  // 工具ID集合
  setToolIds(toolIds: number[]) {
    console.log('setToolIds:', toolIds)
    set({ tool_ids: toolIds })
  },
  // 工具开关
  setToolEnabled(status: boolean) {
    set({ tool_enable: status })
  },
  // 历史/会话切换标记
  setHisType(hisType: boolean) {
    set({ hisType: hisType })
  },
  // 编辑模式标记
  setIsFix(isFix: boolean) {
    set({ is_fix: isFix })
  },
  // 停止/暂停生成标记
  setPaused(isPaused: boolean) {
    set({ isPaused })
  },
  addMessagePair(userMessage: string, assistantMessage: string | JSX.Element, hitPlugin = {}) {
    // 添加用户消息
    const userMsg: ChatMessageType = {
      role: 'user',
      content: [
        {
          type: 'text' as const,
          text: userMessage
        }
      ],
      createTime: Date.now()
    }

    // 添加助手消息
    const assistantMsg: ChatMessageType = {
      role: 'assistant',
      content: [
        {
          type: 'text' as const,
          text: typeof assistantMessage === 'string' ? assistantMessage : assistantMessage.toString(),
          internetCitations: [],
          think: '',
          thinkTime: 0,
          hitPlugin
        }
      ],
      createTime: Date.now() + 1000, // 时间戳加1秒，让消息显示在用户消息后面
      status: 1
    }

    // 直接添加到消息列表
    set((state: chatState & Actions) => ({
      ...state,
      messages: [...state.messages, userMsg, assistantMsg]
    }))
  },
  // 当前正在播放的音频消息ID（互斥控制）
  setAudioPlayingId(id: string | null) {
    set({ audioPlayingId: id })
  },
  // 设置停止视频任务轮询的函数
  setStopVideoTaskPolling(stopFn: () => void | undefined) {
    set({ stopVideoTaskPolling: stopFn })
  },
  /**
   * 更新视频任务状态
   * @param chatId 会话ID
   * @param generateVideo 视频任务信息
   */
  updateVideoTaskStatus(
    chatId: string,
    generateVideo: {
      task_status?: string
      url?: string | null
      name?: string | null
      is_valid?: number
      task_id?: string
    }
  ) {
    const currentChatId = get().chatId
    if (chatId !== currentChatId) {
      return
    }

    const messages = get().messages.filter(msg => msg.role !== 'loading')
    const lastMessage = messages[messages.length - 1]
    const isLastMessageAssistant = lastMessage?.role === 'assistant'

    if (!isLastMessageAssistant) {
      // 如果最后一条不是助手消息，创建一个新的助手消息
      const newMessage = {
        role: 'assistant',
        content: [
          {
            type: 'text',
            text: '',
            generate_video: generateVideo
          }
        ],
        createTime: Date.now(),
        status: 10
      }
      set(state => ({ messages: [...state.messages, newMessage] }))
      return
    }

    // 更新最后一条助手消息的视频任务状态
    const updatedMessage = produce(lastMessage, (draft: ChatMessageType) => {
      if (draft.content && draft.content.length > 0) {
        const content = draft.content[0]
        // 合并视频任务信息，保留已有的其他字段
        content.generate_video = {
          ...content.generate_video,
          ...generateVideo
        }
      }
    })

    const newMessages = [...messages.slice(0, -1), updatedMessage]
    set(state => ({ messages: newMessages }))
  }
}))
